/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  useFloating,
  offset,
  flip,
  size
} from '@floating-ui/react-dom';
import useForkRef from '../hooks/useForkRef';

const Menu = React.forwardRef((props, ref) => {
  const {
    children,
    className,
    anchorElementRef,
    matchAnchorWidth,
    onMenuPlaced,
    placement,
    open,
    style,
    menuListProps
  } = props;

  const {
    x: left, y: top, reference, floating, strategy, refs, placement: usedPlacement, update
  } = useFloating({
    placement,
    middleware: [offset(-1), flip(), size(
      {
        apply: ({ reference: { width } }) => {
          const widthStyle = matchAnchorWidth ? { width: `${width}px` } : {};
          Object.assign(refs.floating.current?.style, widthStyle);
        }
      }
    )],
  });

  useEffect(() => {
    reference(anchorElementRef.current);
  }, [anchorElementRef, reference]);

  useEffect(() => {
    if (onMenuPlaced) {
      onMenuPlaced(usedPlacement);
    }
  }, [onMenuPlaced, usedPlacement]);

  const menuClasses = classNames('sui-z-20 sui-border-1 sui-border-solid sui-border-strong sui-bg-primary',
    className
  );

  const containerRef = useForkRef(floating, ref);

  if (!open) return null;
  return (
    <div
      ref={containerRef}
      className={menuClasses}
      style={{
        ...style,
        position: strategy,
        top: top ?? '',
        left: left ?? ''
      }}
    >
      <ul role="listbox" className="sui-py-2" {...menuListProps}>
        {children}
      </ul>
    </div>
  );
});

Menu.displayName = 'Menu';

Menu.propTypes = {
  /**
   * The elements to populate the Menu with.
   */
  children: PropTypes.node.isRequired,
  /**
   * @ignore
   * Additional css class names to be added.
   */
  className: PropTypes.string,
  /**
   * @ignore
   * Additional inline style to be applied to `Menu`.
   */
  style: PropTypes.object,
  /**
   * Event fired when the `Menu` is displayed and placed in the DOM and its final placement has been decided
   */
  onMenuPlaced: PropTypes.func,
  /**
   * If true, the `Menu` is displayed. Otherwise null.
   */
  open: PropTypes.bool,
  /**
   * A `ref` to an element that acts as an anchor point
   */
  anchorElementRef: PropTypes.shape({
    current: PropTypes.instanceOf(PropTypes.any)
  }).isRequired,
  /**
   * The location where the menu should be located relative to the `anchorElement`.
   * Uses `bottom-start` by default. See all possible options at: https://floating-ui.com/docs/tutorial#placements
   */
  placement: PropTypes.string,
  /**
   * If `true`, the Menu will match the anchor element's width.
   */
  matchAnchorWidth: PropTypes.bool,
  /**
   * The id attribute that is added to the Menu wrapper element
   */
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  /**
   * The prop values that will be added to the menu list element.
   */
  menuListProps: PropTypes.object,
};

Menu.defaultProps = {
  open: false,
  matchAnchorWidth: false,
  placement: 'bottom-start'
};

export { Menu };